import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, CopyIcon } from 'lucide-react';
import { GapTrack } from './GapTrack';
import { PortraitPolaroid } from './PortraitPolaroid';
import { clients, profile } from '../../data/profile';
import { EASE_OUT } from '../../utils/motion';
import { trackEvent } from '../../utils/analytics';
const HEADLINE_WORDS = ['I', 'design', 'products', 'people', 'actually', 'finish.'];
export function Hero() {
  const [copied, setCopied] = useState(false);
  const copyEmail = async () => {
    trackEvent('email_copy', {
      source: 'hero'
    });
    try {
      await navigator.clipboard.writeText(profile.email);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      window.location.href = `mailto:${profile.email}`;
    }
  };
  const fadeUp = (delay: number) => ({
    initial: {
      opacity: 0,
      y: 10
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.3,
      ease: EASE_OUT,
      delay
    }
  });
  return <section id="top" className="scroll-mt-24" data-id="element-260" data-mp="1mv18qdrlofa7">
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_240px] lg:items-start lg:gap-12" data-id="element-261" data-mp="1rvyqwqcm19bp">
      <div className="min-w-0" data-id="element-262" data-mp="1cyerdyyhg6cx">
      <motion.div {...fadeUp(0)} className="mb-7 flex items-center gap-4 lg:hidden" data-id="element-263" data-mp="1czgckw97q98d">
        <img src={profile.portrait} alt={profile.portraitAlt} className="h-16 w-16 shrink-0 rounded-full object-cover object-[center_30%]" data-id="element-264" data-mp="1hwg9agk69zmt" />
        <p className="text-sm leading-snug text-fg-faint" data-id="element-265" data-mp="2alng02ts5vj5">{profile.portraitCaption}</p>
      </motion.div>

      <motion.p {...fadeUp(0)} className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-rust" data-id="element-266" data-mp="1vxkasvnkanve">
        <span aria-hidden className="h-[1.5px] w-[22px] bg-coral" data-id="element-267" data-mp="20usrsswz4lv2" />
        SENIOR PRODUCT DESIGNER · BEHAVIORAL SCIENCE · & AI
      </motion.p>

      <h1 className="mt-5 max-w-2xl text-[40px] font-semibold leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-[56px] dark:text-fg" data-id="element-268" data-mp="2tcelgtand1s9">
        {HEADLINE_WORDS.map((word, i) => <span key={word} className="mr-[0.24em] inline-block overflow-hidden pb-1 align-bottom last:mr-0" data-id="element-269" data-mp="3360z796d59t0" data-mpi={i}>
            <motion.span className="inline-block" initial={{
              y: '105%'
            }} animate={{
              y: 0
            }} transition={{
              duration: 0.3,
              ease: EASE_OUT,
              delay: 0.08 + i * 0.05
            }} data-id="element-270" data-mp="2u7oz8gjlwznw">
              {word}
            </motion.span>
          </span>)}
      </h1>

      <motion.div {...fadeUp(0.38)} className="mt-6" data-id="element-271" data-mp="2fiap3687ie7g">
        <GapTrack data-id="element-272" data-mp="2fhhrovv5111z" />
      </motion.div>

      <motion.p {...fadeUp(0.42)} className="mt-7 max-w-xl text-base leading-relaxed text-fg-muted" data-id="element-273" data-mp="2ajg6a4a8opnc">
        I'm Kat, a senior product designer and psychology student with 9 years in fintech, B2B SaaS and AI. I use
        behavioral science to close the gap between what people plan to do and what they actually do.
      </motion.p>

      <motion.div {...fadeUp(0.48)} className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-4" data-id="element-274" data-mp="1wl9ixi2x281x">
        <a href="#work" className="group inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-[15px] font-semibold text-white shadow-[0_4px_14px_rgba(30,75,72,0.22)] transition-[background-color,transform] duration-150 ease-out hover:bg-ink-deep active:scale-[0.97]" data-id="element-275" data-mp="c14ci4iogdsz">
          See case studies
          <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5" data-id="element-276" data-mp="pzcit31lsrgt" />
        </a>

        <button type="button" onClick={copyEmail} aria-label={`Copy email address ${profile.email}`} className="group inline-flex items-center gap-2 py-0.5 text-[13.5px] font-medium text-fg-muted underline decoration-fg-faint/50 decoration-dotted underline-offset-4 transition-colors duration-150 hover:text-ink hover:decoration-ink/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30" data-id="element-277" data-mp="3ufdnmwb8vgoa">
          <span className={copied ? 'font-semibold text-ink' : ''} data-id="element-278" data-mp="3aptipwya2slj">{copied ? 'Copied!' : profile.email}</span>
          <span className="relative grid h-3.5 w-3.5 place-items-center" data-id="element-279" data-mp="2rkyh633x9udp">
            <AnimatePresence mode="popLayout" initial={false} data-id="element-280" data-mp="2mq15eof9d6x4">
              <motion.span key={copied ? 'check' : 'copy'} initial={{
                  opacity: 0,
                  scale: 0.8
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.8
                }} transition={{
                  duration: 0.15,
                  ease: EASE_OUT
                }} className="absolute" data-id="element-281" data-mp="31e5se4lcg4lp">
                {copied ? <CheckIcon className="h-3.5 w-3.5 text-ink" strokeWidth={2.4} data-id="element-282" data-mp="3kbov6qh7kqzo" /> : <CopyIcon className="h-3.5 w-3.5 text-fg-faint transition-colors duration-150 group-hover:text-ink" data-id="element-283" data-mp="3fcc7k3iijfw1" />}
              </motion.span>
            </AnimatePresence>
          </span>
        </button>
        <span aria-live="polite" className="sr-only" data-id="element-284" data-mp="1kzw0x1lt1zqn">
          {copied ? 'Email copied' : ''}
        </span>
      </motion.div>
      </div>

      <motion.figure initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3,
        ease: EASE_OUT,
        delay: 0.45
      }} className="hidden lg:block lg:pt-2" data-id="element-285" data-mp="j64icgduoc9">
        <PortraitPolaroid data-id="element-286" data-mp="3ruy0a7iom3ix" />
        <figcaption className="mt-3 text-[13px] leading-relaxed text-fg-faint" data-id="element-287" data-mp="3ru6h5w69ty9j">{profile.portraitCaption}</figcaption>
      </motion.figure>
      </div>

      <motion.div {...fadeUp(0.54)} className="mt-12 max-w-[620px] border-t border-line pt-6" data-id="element-288" data-mp="3hxbkzjxh09yf">
        <p className="text-[11.5px] font-semibold uppercase tracking-[0.1em] text-fg-faint" data-id="element-289" data-mp="33xim31bsinfd">Selected clients</p>
        <ul className="mt-4 flex flex-wrap gap-x-8 gap-y-3" data-id="element-290" data-mp="38wdbim49ja8g">
          {clients.map(client => <li key={client} className="text-[14.5px] font-semibold tracking-wide text-fg-faint" data-id="element-291" data-mp="126fc8e9q5cvv">
              {client}
            </li>)}
        </ul>
      </motion.div>
    </section>;
}